<template>
  <div class="rm rm-theme rcm" :style="{ '--rcm-top': `${top}px` }">
    <div class="rcm-head">
      <span v-if="ctl.restaurantName" class="rm-muted">{{ ctl.restaurantName }}</span>
      <h1>{{ $t("recipes") }}</h1>
    </div>

    <div class="rcm-row">
      <button type="button" class="rm-chip rcm-grow" data-open="period" @click="periodSheet = true">
        <v-icon size="16" color="#5f5852">mdi-calendar-blank-outline</v-icon>
        <span class="rm-num">{{ ctl.periodShort }}</span>
        <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
      </button>
      <button type="button" class="rm-chip" :class="{ 'rm-chip--accent': filtersActive }" data-open="filters" @click="filterSheet = true">
        <v-icon size="16" :color="filtersActive ? '#9a3412' : '#1c1917'">mdi-tune-variant</v-icon>
        {{ $t("filters") }}
      </button>
    </div>

    <div class="rcm-row rcm-shifts" role="group" :aria-label="$t('shifts')">
      <button
        v-for="n in allShifts"
        :key="n"
        type="button"
        class="rcm-shift"
        :data-shift="n"
        :aria-pressed="String(ctl.shifts.includes(n))"
        :aria-label="$t('report_mobile.shift_n', { n })"
        @click="ctl.toggleShift(n)"
      >
        {{ $t("report_mobile.shift_short", { n }) }}
      </button>
    </div>

    <div class="rcm-seg">
      <div class="rm-segment" role="group" :aria-label="$t('recipes')">
        <button
          v-for="tab in ctl.tabs"
          :key="tab.key"
          type="button"
          class="rm-segment__btn"
          :class="{ 'is-active': ctl.currentKey === tab.key }"
          :aria-pressed="String(ctl.currentKey === tab.key)"
          @click="ctl.openTab(tab.key)"
        >
          {{ tab.title }}
        </button>
      </div>
    </div>

    <div class="rcm-row">
      <label class="rm-search">
        <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
        <span class="rm-visually-hidden">{{ $t("recipes_shell.search_placeholder") }}</span>
        <input type="search" :value="ctl.search" :placeholder="$t('recipes_shell.search_placeholder')" @input="ctl.setSearch($event.target.value)" />
      </label>
    </div>

    <div v-if="ctl.kpis.length" class="rcm-kpis">
      <div v-for="kpi in ctl.kpis" :key="kpi.key" class="rcm-kpi">
        <span>{{ kpi.label }}</span>
        <b>{{ kpi.value }}</b>
      </div>
    </div>

    <div class="rcm-list">
      <recipes-notes v-if="ctl.hasData && ctl.currentKey === 'meals'"></recipes-notes>
      <recipes-state v-if="ctl.stateKind" :kind="ctl.stateKind"></recipes-state>
      <template v-else-if="ctl.currentKey === 'meals'">
        <recipes-mobile-card
          v-for="(entry, index) in ctl.visibleCards"
          :key="entry.c.name"
          :c="entry.c"
          :rows="entry.rows"
          :index="index"
          :query="entry.q"
          :meal-hit="Boolean(entry.mealHit)"
        ></recipes-mobile-card>
      </template>
      <section v-else class="rcm-card rcm-total">
        <button v-for="row in ctl.totalsShown" :key="row.key" type="button" class="rcm-ing" @click="ingKey = row.key">
          <span class="rcm-ing__l">
            <b><recipe-hit :text="toLatin(row.name)" :query="ctl.search.trim()"></recipe-hit></b>
            <span>{{ perShift(row) }} · {{ ctl.pluralT("recipes_shell.meals_n", row.meals) }}</span>
          </span>
          <span class="rcm-ing__r rcp-num">
            <b>{{ ctl.fq(row.total) }}</b>
            <span class="rcm-unit">&nbsp;{{ row.measure }}</span>
            <span v-if="ctl.price" class="rcm-ing__cost">{{ row.cost == null ? "—" : money(row.cost) }}</span>
          </span>
        </button>
      </section>
      <recipes-zero-note v-if="ctl.hasData && ctl.currentKey === 'meals'"></recipes-zero-note>
    </div>

    <div class="rcm-bar">
      <div class="rcm-bar__main">
        <button
          v-if="ctl.editTotal"
          type="button"
          class="rcm-bar__edits"
          :aria-label="$t('recipes_shell.revert_all_n', { n: ctl.editTotal })"
          @click="ctl.revertAll"
        >
          <i class="rcp-dot"></i>
          <b>{{ ctl.pluralT("recipes_shell.edits_n", ctl.editTotal) }}</b>
          <u>{{ $t("recipes_shell.revert") }}</u>
          <v-icon size="18" class="rcm-bar__undo">mdi-undo-variant</v-icon>
        </button>
        <button
          v-else
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--accent': ctl.price }"
          role="switch"
          :aria-checked="String(ctl.price)"
          @click="ctl.togglePrice"
        >
          <v-icon size="16" :color="ctl.price ? '#9a3412' : '#1c1917'">mdi-cash-multiple</v-icon>
          {{ $t("recipes_shell.prices") }}
        </button>
      </div>
      <button type="button" class="rcm-bar__btn" :disabled="!ctl.canExport" :aria-label="$t('report_mobile.print_pdf')" @click="ctl.print">
        <v-icon size="20" color="#1c1917">mdi-printer-outline</v-icon>
      </button>
      <button type="button" class="rcm-bar__btn" :disabled="!ctl.canExport" :aria-label="$t('report_mobile.download_excel')" @click="ctl.excel">
        <v-icon size="20" color="#15803d">mdi-microsoft-excel</v-icon>
      </button>
      <button type="button" class="rcm-bar__btn" :aria-label="$t('report_mobile.refresh')" @click="ctl.reload">
        <v-icon size="20" color="#1c1917">mdi-refresh</v-icon>
      </button>
    </div>

    <report-mobile-period-sheet v-model="periodSheet" :from="ctl.from" :to="ctl.to" :presets="ctl.presets" @apply="ctl.setPeriod"></report-mobile-period-sheet>
    <recipes-mobile-filter-sheet v-model="filterSheet"></recipes-mobile-filter-sheet>

    <ui-sheet v-model="ingOpen" :ready="Boolean(ingRow)" sheet-class="rcm-sheet-ing" :label="ingRow ? toLatin(ingRow.name) : ''">
      <template #default>
        <div class="rcm-sheet-ing__head">
          <div>
            <h2>{{ toLatin(ingRow.name) }}</h2>
            <p>
              <b>{{ ctl.fq(ingRow.total) }} {{ ingRow.measure }}</b>
              · {{ ctl.pluralT("recipes_shell.meals_n", ingRow.meals) }}
            </p>
          </div>
          <button type="button" class="rm-icon-btn rm-icon-btn--filled" :aria-label="$t('close')" @click="ingKey = null">
            <v-icon size="18" color="#1c1917">mdi-close</v-icon>
          </button>
        </div>
        <ingredient-panel :row-key="ingRow.key"></ingredient-panel>
      </template>
    </ui-sheet>

    <recipes-toast></recipes-toast>
    <recipes-dialog></recipes-dialog>
  </div>
</template>

<script>
import "@/components/report/mobile/reportMobileShell.scss";
import "../recipesShell.scss";
import "./recipesMobile.scss";
import ReportMobilePeriodSheet from "@/components/report/mobile/ReportMobilePeriodSheet.vue";
import IngredientPanel from "../parts/IngredientPanel.vue";
import RecipeHit from "../parts/RecipeHit.vue";
import RecipesDialog from "../parts/RecipesDialog.vue";
import RecipesNotes from "../parts/RecipesNotes.vue";
import RecipesState from "../parts/RecipesState.vue";
import RecipesToast from "../parts/RecipesToast.vue";
import RecipesZeroNote from "../parts/RecipesZeroNote.vue";
import RecipesMobileCard from "./RecipesMobileCard.vue";
import RecipesMobileFilterSheet from "./RecipesMobileFilterSheet.vue";

const DEFAULT_TOP = 64;

/**
 * Recepti na telefonu: period i filteri u dva dugmeta (donji listovi), smjene u boji, Po jelima / Zbirno, pretraga, brojke,
 * kartice jela koje se otvaraju dodirom (polja porcija sa − i +, 44 px) i donja traka (cijene ili "vrati sve", štampa,
 * Excel, osvježi). Namirnica iz zbirne liste otvara donji list sa raspodjelom po jelima. Stanje je u stranici (`recipesCtl`).
 */
export default {
  name: "RecipesMobile",
  components: {
    ReportMobilePeriodSheet,
    IngredientPanel,
    RecipeHit,
    RecipesDialog,
    RecipesNotes,
    RecipesState,
    RecipesToast,
    RecipesZeroNote,
    RecipesMobileCard,
    RecipesMobileFilterSheet,
  },
  inject: { ctl: "recipesCtl" },
  data: () => ({
    allShifts: [1, 2, 3, 4],
    periodSheet: false,
    filterSheet: false,
    ingKey: null,
    top: DEFAULT_TOP,
  }),
  computed: {
    /** Filteri su izmijenjeni: neka jela isključena ili drugi prikaz od podrazumijevanog. */
    filtersActive() {
      return this.ctl.pickedCount < this.ctl.mealOptions.length || this.ctl.view !== this.ctl.defaultView;
    },
    ingRow() {
      return this.ingKey ? this.ctl.ingredientByKey(this.ingKey) : null;
    },
    ingOpen: {
      get() {
        return Boolean(this.ingRow);
      },
      set(open) {
        if (!open) {
          this.ingKey = null;
        }
      },
    },
  },
  watch: {
    "ctl.busy"(busy) {
      if (busy) {
        this.ingKey = null;
      }
    },
  },
  mounted() {
    this.measureTop();
    window.addEventListener("resize", this.measureTop);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.measureTop);
  },
  methods: {
    measureTop() {
      const bar = document.querySelector(".v-app-bar");
      this.top = bar ? Math.round(bar.getBoundingClientRect().height) : DEFAULT_TOP;
    },
    money(value) {
      return [this.ctl.fm(value), this.ctl.currency].filter(Boolean).join(" ");
    },
    /** "Sm. 1 40 · Sm. 2 12" za zbirnu listu (smjene bez količine se izostavljaju). */
    perShift(row) {
      const shifts = [...this.ctl.shifts].sort();
      const parts = row.shown
        .map((value, i) => (value ? `${this.$t("report_mobile.shift_short", { n: shifts[i] })} ${this.ctl.fq(value)}` : ""))
        .filter(Boolean);
      return parts.length ? parts.join(" · ") : "—";
    },
  },
};
</script>
